import { DateTime } from "luxon"
import { Tooltip } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers"
import { useAppLocalization } from "../../../../../../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../../../modules/localization/useDateTime"
import { validateDeliveryDate } from "../../helpers"

const useStyles = makeStyles()((theme) => ({
    box: {
        background: theme.palette.tailwind.red[300],
        color: theme.palette.common.white,
        "&.Mui-disabled:not(.Mui-selected)": {
            color: theme.palette.common.white,
        },
        // A closing date stays enabled for whoever may order on it, so MUI's hover/focus and
        // selected backgrounds apply — they outrank a single class and would wash the red away.
        "&&:hover, &&:focus, &&.Mui-focusVisible": {
            background: theme.palette.tailwind.red[400],
            color: theme.palette.common.white,
        },
        "&&.Mui-selected": {
            background: theme.palette.tailwind.red[600],
            color: theme.palette.common.white,
            "&:hover, &:focus": {
                background: theme.palette.tailwind.red[700],
            },
        },
    },
    smallRoot: {
        width: "1.75rem",
        height: "1.75rem",
        margin: ".25rem .375rem",
    },
}))
type CustomDayProps = PickersDayProps<unknown> & {
    closingDates: string[]
    allowedWeekdays: number[]
    canOverrideClosingDates: boolean
    grandfatheredDate?: DateTime | null
    allowPastDates: boolean
}

export function CustomDay(props: CustomDayProps) {
    const { day, closingDates, allowedWeekdays, canOverrideClosingDates, grandfatheredDate, allowPastDates, ...other } =
        props
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const dayDateTime = day instanceof DateTime ? day : dateTimeFactory.fromJSDate(day as Date)
    const today = dateTimeFactory.now().startOf("day")

    const { isValidDeliveryDate, isSelectable, isGrandfathered } = validateDeliveryDate(
        dayDateTime,
        { allowedWeekdays, closingDates, canOverrideClosingDates, grandfatheredDate },
        dateTimeFactory,
    )

    const isPast = !allowPastDates && dayDateTime < today
    const isUnavailable = !isValidDeliveryDate
    const isDisabled = !isSelectable || isPast

    // This one red day is clickable while its neighbours are not, so it says why.
    const tooltipId = isGrandfathered
        ? "create-order-delivery-times-dialog-delivery-date-kept-tooltip"
        : "create-order-delivery-times-dialog-delivery-date-close-tooltip"

    return (
        <Tooltip title={isUnavailable ? l10n.getString(tooltipId) : ""} arrow>
            <span>
                <PickersDay
                    {...other}
                    day={day}
                    disabled={isDisabled}
                    className={isUnavailable ? classes.box : undefined}
                    classes={{ root: classes.smallRoot }}
                />
            </span>
        </Tooltip>
    )
}
